import { CreateWorkerInvitesPayload, InviteSource, InviteTargetItem } from '@vyce/core/src/api/types';

import { INVITE_MODULES_CONFIG } from '../constants';
import { SelectionState, TargetsState } from '../types';

export const buildInviteTargetItems = (targets: TargetsState): InviteTargetItem[] => {
  const items: InviteTargetItem[] = targets.teamIds.map(id => ({ resource_type: 'team', resource_id: id }));

  targets.locationIds.forEach(locationId => {
    (targets.shiftIdsByLocation[locationId] ?? []).forEach(shiftId => {
      items.push({ resource_type: 'location', resource_id: locationId, shift_id: shiftId });
    });
  });

  // pay_scheme is required per pay schedule item — without it the schedules are not sent
  const { payScheme } = targets;
  if (payScheme) {
    targets.payScheduleIds.forEach(id => {
      items.push({ resource_type: 'pay_schedule', resource_id: id, pay_scheme: payScheme });
    });
  }

  return items;
};

/**
 * Maps the dialog state onto the /workers invite body. Bulk scope → `source`, unticked
 * rows → `exclude_user_ids`, explicit picks → `user_ids`. Overlap between picks and the
 * scope is fine (the backend de-duplicates); an exclusion always wins over an older pick.
 */
export const buildWorkerInvitesPayload = (
  selection: SelectionState,
  targets: TargetsState,
  sendReport = true
): CreateWorkerInvitesPayload => {
  const source: InviteSource = { team_ids: [], location_ids: [], pay_schedule_ids: [], all_users: false };
  const { bulk } = selection;

  if (bulk?.kind === 'all') {
    source.all_users = true;
  } else if (bulk?.kind === 'resource') {
    source[INVITE_MODULES_CONFIG[bulk.module].sourceKey] = [bulk.resourceId];
  }

  // Exclusions are only meaningful against a scope
  const exclude_user_ids = bulk ? Array.from(selection.excluded.keys()) : [];
  const excludedSet = new Set(exclude_user_ids);
  const user_ids = Array.from(selection.picked.keys()).filter(id => !excludedSet.has(id));

  return {
    items: buildInviteTargetItems(targets),
    contacts: selection.phones.map(phone => ({ phone })),
    user_ids,
    source,
    exclude_user_ids,
    send_report: sendReport,
  };
};
